import { state } from "./state";
import { escapeHtml, escapeAttr, kindEmoji, byId } from "./dom";
import { isLibrary, latestVersion } from "./model";
import type { Asset, Prefab } from "./model";
import { clearSelection, selectAsset } from "./router";
import { bindTagPills, tagPillHtml } from "./grid";

export function renderDetail(): void {
  const detail = byId("detail");
  if (state.selectedAsset) {
    renderAssetDetail();
  } else if (state.selectedPrefab) {
    renderPrefabDetail();
  } else {
    detail.hidden = true;
    detail.innerHTML = "";
  }
}

function renderAssetDetail(): void {
  const detail = byId("detail");
  const { prefab, id } = state.selectedAsset!;
  // The asset grid only shows latest-revision assets, so resolve to the
  // latest matching entry to stay consistent and pin a correct command.
  const matches = state.index!.assets.filter(
    (a) => a.prefab === prefab && a.id === id,
  );
  const asset = matches.length
    ? matches.find(
        (a) => a.version === latestVersion(matches.map((m) => m.version)),
      )
    : undefined;
  if (!asset) {
    detail.hidden = false;
    detail.innerHTML = `<div class="empty">asset not found: ${escapeHtml(prefab)} / ${escapeHtml(id)}</div>`;
    return;
  }
  const featureMembership = featuresContaining(prefab, id);
  const cmd = `gmx prefab add ${prefab}@${asset.version}:${id}`;

  let preview: string;
  if (asset.kind === "sound") {
    const soundUrl = soundAssetUrl(asset);
    preview = soundUrl
      ? `<audio controls preload="none" src="${escapeAttr(soundUrl)}"></audio>`
      : `<div class="placeholder">${kindEmoji(asset.kind)}</div>`;
  } else if (asset.thumbnail) {
    preview = `<img src="${escapeAttr(asset.thumbnail)}" alt="${escapeAttr(asset.id)}"/>`;
  } else {
    preview = `<div class="placeholder">${kindEmoji(asset.kind)}</div>`;
  }

  const dims =
    asset.width != null && asset.height != null
      ? `${asset.width}×${asset.height}`
      : null;
  const dl: ([string, string] | null)[] = [
    ["id", asset.id],
    ["kind", asset.kind],
    ["prefab", `${prefab}@${asset.version}`],
    asset.category ? ["category", asset.category] : null,
    dims ? ["dimensions", dims] : null,
    asset.frame_count != null ? ["frames", String(asset.frame_count)] : null,
  ];
  const rows = dl.filter((r): r is [string, string] => r !== null);

  detail.hidden = false;
  detail.innerHTML = `
    <header>
      <h2>${escapeHtml(asset.id)}</h2>
      <button class="close-btn" id="close-detail" title="close">×</button>
    </header>
    <div class="preview">${preview}</div>
    <dl>${rows.map(([k, v]) => `<dt>${escapeHtml(k)}</dt><dd>${escapeHtml(v)}</dd>`).join("")}</dl>
    ${asset.description ? `<p>${escapeHtml(asset.description)}</p>` : ""}
    ${(asset.tags || []).length ? `<dt style="font-size:10px;color:var(--text-dim);text-transform:uppercase;margin-bottom:4px">tags</dt><div class="tags">${asset.tags!.map(tagPillHtml).join("")}</div>` : ""}
    ${featureMembership.length ? `<dt style="font-size:10px;color:var(--text-dim);text-transform:uppercase;margin:14px 0 4px">features</dt><div class="tags">${featureMembership.map((f) => `<span class="pill">${escapeHtml(f)}</span>`).join("")}</div>` : ""}
    ${copyBox("copy command", cmd)}
    ${copyBox("run demo", demoCommand(prefab, asset.version))}
  `;
  bindDetailControls();
}

function renderPrefabDetail(): void {
  const detail = byId("detail");
  const { id, version } = state.selectedPrefab!;
  const prefab = resolvePrefab(id, version);
  if (!prefab) {
    detail.hidden = false;
    detail.innerHTML = `<div class="empty">prefab not found: ${escapeHtml(id)}</div>`;
    return;
  }
  if (isLibrary(prefab)) {
    renderLibraryDetail(prefab);
    return;
  }
  // Show the opened revision's own assets, not assets across all revisions.
  const assets = state.index!.assets.filter(
    (a) => a.prefab === id && a.version === prefab.version,
  );
  const cmd = `gmx prefab add ${id}@${prefab.version}`;
  const thumb = prefab.thumbnail
    ? `<img src="${escapeAttr(prefab.thumbnail)}" alt="${escapeAttr(prefab.id)}" onerror="this.replaceWith(Object.assign(document.createElement('span'),{className:'placeholder',textContent:'📦'}))"/>`
    : `<div class="placeholder">📦</div>`;
  // Hide the implicit "default" feature group — it conveys nothing; only
  // surface genuinely named optional features.
  const namedFeatures = (prefab.features || []).filter((f) => f !== "default");
  const dl: ([string, string] | null)[] = [
    ["id", escapeHtml(prefab.id)],
    ["version", escapeHtml(prefab.version)],
    licenseRow(prefab),
    ["assets", escapeHtml(String(prefab.asset_count))],
    prefab.category ? ["category", escapeHtml(prefab.category)] : null,
  ];
  const rows = dl.filter((r): r is [string, string] => r !== null);

  detail.hidden = false;
  detail.innerHTML = `
    <header>
      <h2>${escapeHtml(prefab.id)}</h2>
      <button class="close-btn" id="close-detail" title="close">×</button>
    </header>
    <div class="preview">${thumb}</div>
    <dl>${rows.map(([k, v]) => `<dt>${escapeHtml(k)}</dt><dd>${v}</dd>`).join("")}</dl>
    ${prefab.description ? `<p>${escapeHtml(prefab.description)}</p>` : ""}
    ${namedFeatures.length ? `<dt style="font-size:10px;color:var(--text-dim);text-transform:uppercase;margin:14px 0 4px">features</dt><ul class="features">${namedFeatures.map((f) => `<li><span class="feature-name">${escapeHtml(f)}</span></li>`).join("")}</ul>` : ""}
    ${copyBox("copy command", cmd)}
    ${copyBox("run demo", demoCommand(prefab.id, prefab.version))}
    <dt style="font-size:10px;color:var(--text-dim);text-transform:uppercase;margin:14px 0 4px">asset list (${assets.length})</dt>
    <div class="tags" id="prefab-asset-pills">${assets.map((a) => `<button class="pill pill-asset" type="button" title="${escapeAttr(a.kind)}" data-asset-prefab="${escapeAttr(a.prefab)}" data-asset-id="${escapeAttr(a.id)}">${escapeHtml(a.id)}</button>`).join("")}</div>
  `;
  bindDetailControls();
}

// Bump when an already-published, `immutable`-cached text file (LICENSE) is
// re-uploaded with corrected metadata: changing this query busts every browser
// still pinned to the stale copy, since `immutable` otherwise blocks any
// revalidation at the same URL. (LICENSE was first served as octet-stream.)
const LICENSE_CACHE_BUST = "2";

// Absolute origin of the published catalog. The UI is served from here too, but
// `gmx run` needs a fully-qualified URL, and the demo only lives on the CDN —
// so hardcode the canonical host rather than deriving it (which would be
// `localhost` in the dev preview).
const CATALOG_ORIGIN = "https://prefabs.gx.games";

/** The `gmx run <url>` command that launches this prefab's published demo. */
function demoCommand(id: string, version: string): string {
  return `gmx run ${CATALOG_ORIGIN}/prefabs/${id}/${version}/demo.tomlz`;
}

/** A labelled, copyable command box (label heading + `<code>` + copy button). */
function copyBox(label: string, command: string): string {
  return (
    `<dt style="font-size:10px;color:var(--text-dim);text-transform:uppercase;margin:14px 0 4px">${escapeHtml(label)}</dt>` +
    `<div class="copy-cmd"><code>${escapeHtml(command)}</code><button class="copy-btn" type="button">copy</button></div>`
  );
}

/**
 * A `["license", html]` dl row linking the SPDX id to the prefab's LICENSE
 * file on the CDN, or null when the index carries no license for this prefab.
 */
function licenseRow(prefab: Prefab): [string, string] | null {
  if (!prefab.license) return null;
  const url = `prefabs/${prefab.id}/${prefab.version}/LICENSE?v=${LICENSE_CACHE_BUST}`;
  return [
    "license",
    `<a href="${escapeAttr(url)}" target="_blank" rel="noreferrer">${escapeHtml(prefab.license)}</a>`,
  ];
}

function renderLibraryDetail(prefab: Prefab): void {
  const detail = byId("detail");
  const cmd = `gmx prefab add ${prefab.id}@${prefab.version}`;
  const thumb = prefab.thumbnail
    ? `<img src="${escapeAttr(prefab.thumbnail)}" alt="${escapeAttr(prefab.id)}" onerror="this.replaceWith(Object.assign(document.createElement('span'),{className:'placeholder',textContent:'📚'}))"/>`
    : `<div class="placeholder">📚</div>`;
  const readmeUrl = `prefabs/${prefab.id}/${prefab.version}/README.md`;
  const dl: ([string, string] | null)[] = [
    ["id", escapeHtml(prefab.id)],
    ["version", escapeHtml(prefab.version)],
    licenseRow(prefab),
  ];
  const rows = dl.filter((r): r is [string, string] => r !== null);
  detail.hidden = false;
  detail.innerHTML = `
    <header>
      <h2>${escapeHtml(prefab.id)} <span class="library-badge">library</span></h2>
      <button class="close-btn" id="close-detail" title="close">×</button>
    </header>
    <div class="preview">${thumb}</div>
    <dl>${rows.map(([k, v]) => `<dt>${escapeHtml(k)}</dt><dd>${v}</dd>`).join("")}</dl>
    ${prefab.description ? `<p>${escapeHtml(prefab.description)}</p>` : ""}
    ${copyBox("copy command", cmd)}
    ${copyBox("run demo", demoCommand(prefab.id, prefab.version))}
    <section class="readme" id="library-readme"><div class="empty">loading README…</div></section>
  `;
  bindDetailControls();
  fetch(readmeUrl)
    .then((r) =>
      r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`)),
    )
    .then((md) => {
      const el = document.getElementById("library-readme");
      if (el) el.innerHTML = renderMarkdown(md, readmeUrl);
    })
    .catch(() => {
      const el = document.getElementById("library-readme");
      if (el) el.innerHTML = `<div class="empty">README not available</div>`;
    });
}

function renderMarkdown(md: string, baseUrl: string): string {
  const escaped = escapeHtml(md);
  const lines = escaped.split(/\r?\n/);
  const out: string[] = [];
  let inCode = false;
  let inList = false;
  for (const line of lines) {
    if (line.startsWith("```")) {
      if (inCode) {
        out.push("</code></pre>");
        inCode = false;
      } else {
        out.push("<pre><code>");
        inCode = true;
      }
      continue;
    }
    if (inCode) {
      out.push(line);
      continue;
    }
    const h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) {
      if (inList) {
        out.push("</ul>");
        inList = false;
      }
      const n = h[1].length;
      out.push(`<h${n}>${linkify(h[2], baseUrl)}</h${n}>`);
      continue;
    }
    const li = line.match(/^[-*]\s+(.*)$/);
    if (li) {
      if (!inList) {
        out.push("<ul>");
        inList = true;
      }
      out.push(`<li>${linkify(li[1], baseUrl)}</li>`);
      continue;
    }
    if (inList) {
      out.push("</ul>");
      inList = false;
    }
    if (line.trim() === "") {
      out.push("");
      continue;
    }
    out.push(`<p>${linkify(line, baseUrl)}</p>`);
  }
  if (inList) out.push("</ul>");
  if (inCode) out.push("</code></pre>");
  return out.join("\n");
}

function linkify(s: string, baseUrl: string): string {
  return s.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    // `s` is already HTML-escaped, so `href` is attribute-safe; resolveHref
    // only rewrites the path, so we must not re-escape (would double-encode).
    (_m, text, href) =>
      `<a href="${resolveHref(href, baseUrl)}" target="_blank" rel="noreferrer">${text}</a>`,
  );
}

/**
 * Resolve a markdown link target relative to the README it came from, so a
 * link like `[LICENSE](LICENSE)` points at the prefab's own directory rather
 * than the site root. Absolute URLs, protocol-relative, root-relative, and
 * in-page anchors are left untouched.
 */
function resolveHref(href: string, baseUrl: string): string {
  if (/^([a-z][a-z0-9+.-]*:|\/\/|\/|#)/i.test(href)) return href;
  try {
    const url = new URL(href, new URL(baseUrl, location.href));
    // Same cache-bust as licenseRow: a README's `[LICENSE](LICENSE)` resolves
    // to the immutable LICENSE object, so stale octet-stream browser copies
    // need a fresh URL to bypass them. Only the LICENSE file, only when the
    // link doesn't already carry a query.
    if (!url.search && /\/LICENSE$/.test(url.pathname)) {
      url.search = `?v=${LICENSE_CACHE_BUST}`;
    }
    return url.href;
  } catch {
    return href;
  }
}

function bindDetailControls(): void {
  const detail = byId("detail");
  document
    .getElementById("close-detail")
    ?.addEventListener("click", clearSelection);
  document.querySelectorAll("#prefab-asset-pills .pill-asset").forEach((pill) => {
    pill.addEventListener("click", () => {
      const el = pill as HTMLElement;
      const p = el.dataset.assetPrefab;
      const a = el.dataset.assetId;
      if (p && a) selectAsset(p, a);
    });
  });
  bindTagPills(detail);
  // Wire every copy box (copy command, run demo) to copy its own `<code>` text.
  detail.querySelectorAll<HTMLButtonElement>(".copy-cmd .copy-btn").forEach((btn) => {
    btn.addEventListener("click", async () => {
      const text = btn.parentElement?.querySelector("code")?.textContent ?? "";
      try {
        await navigator.clipboard.writeText(text);
        btn.textContent = "copied!";
        btn.classList.add("copied");
        setTimeout(() => {
          btn.textContent = "copy";
          btn.classList.remove("copied");
        }, 1200);
      } catch {
        btn.textContent = "copy failed";
      }
    });
  });
}

/** The prefab entry for `id` at `version`, or its latest revision when null. */
function resolvePrefab(id: string, version: string | null): Prefab | undefined {
  const matches = state.index!.prefabs.filter((p) => p.id === id);
  if (matches.length === 0) return undefined;
  if (version) return matches.find((p) => p.version === version);
  const latest = latestVersion(matches.map((p) => p.version));
  return matches.find((p) => p.version === latest);
}

function featuresContaining(prefabId: string, _assetId: string): string[] {
  const prefab = state.index!.prefabs.find((p) => p.id === prefabId);
  if (!prefab) return [];
  return prefab.features?.length ? prefab.features : [];
}

function soundAssetUrl(asset: Asset): string | null {
  return asset.media_url || null;
}
